// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import MuiTextField, { StandardTextFieldProps } from '@mui/material/TextField';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { InputAdornment, IconButton, FormLabel } from '@mui/material';
import { Field } from 'mobx-react-form';
import { observer } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';
import cx from 'classnames';

import Ellipsis from '@shared/components/Ellipsis';
import { getTestAttr } from '@shared/utils/common';
import { getScrollAttr } from '@shared/utils/form';

export enum NormalizeType {
  onChange,
  onBlur
}

export interface TextFieldProps extends Omit<StandardTextFieldProps, 'classes'> {
  className?: string;
  classes?: Record<string, any>;
  testAttributeArgs: {
    module: string;
    elementDescription: string;
  };
  field?: Field;
  label?: React.ReactNode;
  withShowHidePassword?: boolean;
  normalizeType?: NormalizeType;
  normalize?: (value: string) => string | number;
}

type Size = TextFieldProps['size'];

const defaultSize = 'medium';

export const normalizeNumber = (value: string): string | number => {
  const numberValue = Number(value);
  const isNumber = value && !isNaN(numberValue);

  if (isNumber) {
    return numberValue;
  }

  return value;
};

@observer
class TextField extends React.Component<TextFieldProps> {
  static defaultProps = {
    size: defaultSize as Size,
  };

  @observable showPassword = false;

  constructor(props: TextFieldProps) {
    super(props);
    makeObservable(this);
  }

  private get endAdornment() {
    const { withShowHidePassword, testAttributeArgs: { module } } = this.props;
    const classes = withStyles.getClasses(this.props);

    if (withShowHidePassword && module) {
      return (
        <InputAdornment
          variant="filled"
          position="end"
        >
          <IconButton
            {...getTestAttr(module, 'password-visibility', 'btn')}
            className={classes.showPasswordBtn}
            aria-label="Toggle password visibility"
            onClick={this.handleClickShowPassword}
          >
            {this.showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </InputAdornment>
      );
    }

    return null;
  }

  @action private handleClickShowPassword = () => {
    this.showPassword = !this.showPassword;
  }

  private handleBlur = (e) => {
    e.persist();

    const { field, normalize, onBlur } = this.props;

    if (onBlur) {
      onBlur(e);
    }

    if (field) {
      const value = e.target.value;

      field.onBlur(e);

      if (normalize) {
        field.set('value', normalize(value));
        field.validate();
      }
    }
  }

  private handleChange = (e) => {
    e.persist();

    const { field, normalize, normalizeType, onChange } = this.props;

    if (onChange) {
      onChange(e);
    }

    if (field) {
      const value = e.target.value;
      const finalValue = normalize && (normalizeType === NormalizeType.onChange) ? normalize(value) : value;

      field.set('value', finalValue);
    }
  }

  render() {
    const {
      field,
      label,
      InputProps,
      withShowHidePassword,
      type,
      size = defaultSize,
      testAttributeArgs,
      inputProps,
      fullWidth,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      normalizeType, onBlur, onChange, normalize,
      helperText,
      ...otherProps
    } = this.props;
    const classes = withStyles.getClasses(this.props);

    const {
      labelRoot: labelRootClass,
      input: inputClass,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      showPasswordBtn,
      rootFullWidth: rootFullWidthClass,
      errorText: errorTextClass,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      medium: mediumClass, small: smallClass,
      ...otherClasses
    } = classes;

    const sizeClasses = {
      medium: classes.medium, 
      small: classes.small
    };

    const errorText = helperText || field?.error;
    const hasError = Boolean(helperText || field?.error);
    // TODO wrong logic with fullWidth, root class

    return (
      <div className={cx(classes.root, { [rootFullWidthClass]: fullWidth })}>
        {label && <FormLabel classes={{ root: labelRootClass }} component="legend">{label}</FormLabel>}
        <MuiTextField
          {...(field ? field.bind() : {})}
          type={withShowHidePassword
            ? this.showPassword ? 'text' : 'password'
            : field?.type || type
          }
          margin="normal"
          variant="standard"
          error={hasError}
          helperText={null}
          fullWidth={fullWidth}
          classes={otherClasses}
          InputProps={{
            classes: {
              root: cx(inputClass, sizeClasses[size])
            },
            endAdornment: this.endAdornment,
            ...InputProps,
          }}
          inputProps={{
            ...getTestAttr(testAttributeArgs.module, `${testAttributeArgs.elementDescription}`, 'input'),
            ...getScrollAttr(field?.name || otherProps.name),
            ...inputProps
          }}
          onBlur={this.handleBlur}
          onChange={this.handleChange}
          {...otherProps}
        />
        {hasError && <Ellipsis classes={{ root: errorTextClass }} text={errorText} />}
      </div>
    );
  }
}

const TextFieldStyled = withStyles(
  TextField, 
  (theme, props) => ({
    "root": {
      position: 'relative',
    },
    "rootFullWidth": {
      width: '100%'
    },
    "input": {},
    "medium": {},
    "small": {
      height: 36,
      fontSize: 14,
    },
    "labelRoot": {
      margin: theme.spacing(5, 0, 1)
    },
    "showPasswordBtn": {
      padding: theme.spacing(2),
  
      '& svg': {
        fontSize: 24,
        color: theme.palette.colors.greyLight3
      }
    },
    "errorText": {
      ...theme.shortcuts.errorText,
    }
  })
);

export default TextFieldStyled;